import { useMemo, useState, type ChangeEvent } from 'react';
import { clearAdminToken, clearPlannedSquad, getAdminToken, getLatestPlannedSquad, hasAdminToken, savePlannedSquad, setAdminToken, type BootstrapResponse, type BramxCompareResponse, type PlannedSquadPayload } from '../lib/api';

interface Props {
  duel: BramxCompareResponse | null;
  entryA: string;
  targetGameweek: number | null | undefined;
  elements: BootstrapResponse['elements'];
  planned: PlannedSquadPayload | null;
  simulatedPlan?: PlannedSquadPayload | null;
  onChange(plan: PlannedSquadPayload | null): void;
  onRun(plan: PlannedSquadPayload): void | Promise<void>;
  onRefreshPublicData?(): void | Promise<void>;
}

function simFingerprint(plan: PlannedSquadPayload | null | undefined): string {
  if (!plan) return 'NONE';
  return JSON.stringify({
    gameweek: plan.gameweek,
    activeChip: plan.activeChip ?? null,
    initialFtCount: plan.initialFtCount ?? null,
    ftCount: plan.ftCount ?? null,
    transfersMade: plan.transfersMade ?? null,
    hitCost: plan.hitCost ?? null,
    routeLabel: plan.routeLabel ?? null,
    picks: [...plan.picks]
      .sort((a, b) => a.position - b.position)
      .map((pick) => ({ element: pick.element, position: pick.position, isCaptain: pick.isCaptain, isViceCaptain: pick.isViceCaptain })),
  });
}

function priceOf(element: BootstrapResponse['elements'][number] | null | undefined): number | null {
  return typeof element?.now_cost === 'number' ? element.now_cost / 10 : null;
}

function fmtPrice(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : `£${value.toFixed(1)}`;
}

export function DecisionLab({ duel, entryA, targetGameweek, elements, planned, simulatedPlan, onChange, onRun, onRefreshPublicData }: Props) {
  const [outId, setOutId] = useState('');
  const [inId, setInId] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [runningDraft, setRunningDraft] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [adminInput, setAdminInput] = useState(() => getAdminToken());
  const [adminUnlocked, setAdminUnlockedState] = useState(() => hasAdminToken());
  const elementMap = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements]);
  const activePlan = planned && targetGameweek && planned.gameweek === targetGameweek ? planned : null;

  const unlockAdmin = async () => {
    const token = adminInput.trim();
    if (!token) { setMessage('ADMIN TOKEN ontbreekt.'); return; }
    setAdminToken(token);
    setAdminUnlockedState(true);
    setMessage('ADMIN TOKEN CHECK · serverplan wordt vers geladen…');

    if (!/^\d+$/.test(entryA) || !targetGameweek) {
      setMessage('ADMIN SESSION UNLOCKED · geen geldige entry/GW om serverplan te laden.');
      return;
    }

    try {
      const server = await getLatestPlannedSquad(entryA, targetGameweek);
      if (server.plan?.gameweek === targetGameweek) {
        onChange(server.plan);
        const sellsKnown = server.plan.picks.filter((pick) => pick.sellingPrice != null).length;
        setMessage(`SERVER PLAN LOADED · GW${targetGameweek} · FT ${server.plan.ftCount ?? '—'} · ITB ${server.plan.cashItb ?? '—'} · SELL ${sellsKnown}/15.`);
      } else {
        setMessage(`ADMIN OK · geen opgeslagen serverplan gevonden voor GW${targetGameweek}.`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (/ADMIN_AUTH_REQUIRED|ADMIN_TOKEN/i.test(message)) {
        clearAdminToken();
        setAdminUnlockedState(false);
      }
      setMessage(`SERVER PLAN LOAD FAILED · ${message}`);
    }
  };
  const lockAdmin = () => {
    clearAdminToken();
    setAdminUnlockedState(false);
    setAdminInput('');
    setMessage('ADMIN SESSION LOCKED · lokaal plan blijft in deze browser beschikbaar.');
  };
  const adminControls = (
    <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/50 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[220px] flex-1"><span className="input-label">ADMIN PLAN ACCESS · SESSION ONLY</span><input className="input" type="password" autoComplete="off" value={adminInput} onChange={(event: ChangeEvent<HTMLInputElement>) => setAdminInput(event.target.value)} placeholder="ADMIN_TOKEN uit .env" /></label>
        <button type="button" className="btn-secondary" onClick={() => void unlockAdmin()}>{adminUnlocked ? 'RE-UNLOCK' : 'UNLOCK SERVER PLAN'}</button>
        {adminUnlocked && <button type="button" className="btn-secondary" onClick={lockAdmin}>LOCK</button>}
      </div>
      <p className="mt-2 text-[9px] text-slate-600">Token blijft alleen in sessionStorage en wordt niet in de bundle of URL gezet. Zonder unlock blijft server-side Planned Squad/replay privé.</p>
    </div>
  );

  const initialize = () => {
    if (!duel || !targetGameweek) return;
    const owned = duel.players
      .filter((player) => player.positionA !== null)
      .sort((a, b) => (a.positionA ?? 99) - (b.positionA ?? 99));
    if (owned.length !== 15) {
      setMessage('Kan plan niet initialiseren: geen complete 15-man Bram squad in deze snapshot.');
      return;
    }
    onChange({
      gameweek: targetGameweek,
      routeLabel: 'ROLL',
      activeChip: null,
      initialFtCount: 1,
      ftCount: 1,
      transfersMade: 0,
      hitCost: 0,
      cashItb: null,
      picks: owned.map((player) => ({
        element: player.elementId,
        position: player.positionA!,
        isCaptain: player.captainA,
        isViceCaptain: player.viceCaptainA,
        multiplier: player.multiplierA,
        purchasePrice: null,
        sellingPrice: null,
      })),
    });
    setMessage('PLANNED squad aangemaakt · CHIP = NONE · publieke CURRENT prices komen automatisch uit FPL. Vul CASH ITB en alleen de officiële SELL prices in van kandidaat-OUT spelers die je door de optimizer wilt laten onderzoeken.');
  };

  if (!duel || duel.scope !== 'NEXT_PREVIEW') {
    return (
      <section className="panel">
        <div className="eyebrow">DECISION LAB</div>
        <h2 className="mt-1 text-lg font-black text-white">Pre-deadline planning</h2>
        <p className="mt-2 text-xs leading-5 text-slate-600">Decision Lab wordt actief in PREVIEW. Tijdens LIVE gebruikt BRAM-X alleen de gepubliceerde target-GW picks.</p>
      </section>
    );
  }

  if (!activePlan) {
    return (
      <section className="panel">
        <div className="eyebrow">DECISION LAB · P0</div>
        <h2 className="mt-1 text-lg font-black text-white">Maak Bram's echte GW{targetGameweek ?? '—'} plan</h2>
        <p className="mt-2 text-xs leading-5 text-slate-500">Zonder plan is Bram net als de rival slechts een <strong className="text-slate-300">LAST-PUBLISHED SCENARIO</strong>. Initialiseer de 15-man squad en wijzig daarna XI, bench, C/VC en route. Publieke prijzen worden automatisch uit FPL geladen.</p>
        {adminControls}
        <button type="button" className="btn-primary mt-4" onClick={initialize}>USE LAST SQUAD AS PLAN</button>
        {message && <p className="mt-2 text-[10px] text-amber-300">{message}</p>}
      </section>
    );
  }

  const ordered = [...activePlan.picks].sort((a, b) => a.position - b.position);
  const selectedIds = new Set(activePlan.picks.map((pick) => pick.element));
  const outPick = activePlan.picks.find((pick) => String(pick.element) === outId) ?? null;
  const outElement = outPick ? elementMap.get(outPick.element) : null;
  const incoming = inId ? elementMap.get(Number(inId)) ?? null : null;
  const candidates = elements
    .filter((element) => !selectedIds.has(element.id) && (!outElement?.element_type || element.element_type === outElement.element_type))
    .sort((a, b) => (a.web_name || a.second_name).localeCompare(b.web_name || b.second_name));

  const patchPlan = (patch: Partial<PlannedSquadPayload>) => onChange({ ...activePlan, ...patch });
  const captainPick = activePlan.picks.find((pick) => pick.isCaptain) ?? null;
  const vicePick = activePlan.picks.find((pick) => pick.isViceCaptain) ?? null;
  const captainName = captainPick ? elementMap.get(captainPick.element)?.web_name ?? elementMap.get(captainPick.element)?.second_name ?? String(captainPick.element) : '—';
  const viceName = vicePick ? elementMap.get(vicePick.element)?.web_name ?? elementMap.get(vicePick.element)?.second_name ?? String(vicePick.element) : '—';
  const draftChanged = simFingerprint(activePlan) !== simFingerprint(simulatedPlan);
  const outCurrent = priceOf(outElement);
  const inCurrent = priceOf(incoming);
  const cashAfter = activePlan.cashItb != null && outPick?.sellingPrice != null && inCurrent != null
    ? Math.round((activePlan.cashItb + outPick.sellingPrice - inCurrent) * 10) / 10
    : null;

  const transferState = (plan: PlannedSquadPayload, initialOverride?: number, transfersOverride?: number, chipOverride?: string | null) => {
    const transfersMade = Math.max(0, Math.floor(transfersOverride ?? plan.transfersMade ?? 0));
    const initialFtCount = Math.max(0, Math.min(5, Math.floor(initialOverride ?? plan.initialFtCount ?? ((plan.ftCount ?? 1) + transfersMade))));
    const chip = chipOverride === undefined ? plan.activeChip ?? null : chipOverride;
    const noHitChip = chip === 'wildcard' || chip === 'freehit';
    return {
      initialFtCount,
      transfersMade,
      ftCount: noHitChip ? initialFtCount : Math.max(0, initialFtCount - transfersMade),
      hitCost: noHitChip ? 0 : Math.max(0, transfersMade - initialFtCount) * 4,
    };
  };

  const setStartingFt = (value: number) => {
    const transfer = transferState(activePlan, value);
    onChange({ ...activePlan, ...transfer });
  };

  const setChip = (value: string | null) => {
    const transfer = transferState(activePlan, undefined, undefined, value);
    onChange({ ...activePlan, activeChip: value, ...transfer });
  };

  const savePlan = async (runAfter = false) => {
    if (!entryA) {
      setMessage('SERVER SAVE NIET MOGELIJK · Bram Entry ID ontbreekt.');
      return;
    }
    if (!adminUnlocked) {
      setMessage('UNLOCK REQUIRED · unlock eerst ADMIN PLAN ACCESS voor private save/sim.');
      return;
    }
    setSaving(true);
    try {
      const response = await savePlannedSquad(entryA, activePlan);
      const saved = response.plan ?? activePlan;
      if (response.plan) onChange(response.plan);
      if (runAfter) {
        setRunningDraft(true);
        setMessage('SERVER SAVED · RUN 5K STARTED…');
        await onRun(saved);
        setMessage(`SERVER SAVED + RUN 5K COMPLETE · ${response.savedAt ? new Date(response.savedAt).toLocaleString() : 'OK'}`);
      } else {
        setMessage(`SERVER SAVED · ${response.savedAt ? new Date(response.savedAt).toLocaleString() : 'OK'}`);
      }
    } catch (error) {
      setMessage(`SAVE FAILED · ${error instanceof Error ? error.message : 'server save failed'} · lokale draft blijft behouden.`);
    } finally {
      setSaving(false);
      if (runAfter) setRunningDraft(false);
    }
  };

  const runDraft = async () => {
    if (!adminUnlocked) {
      setMessage('UNLOCK REQUIRED · private Planned Squad sim vereist ADMIN PLAN ACCESS.');
      return;
    }
    if (runningDraft) return;
    setRunningDraft(true);
    setMessage('RUN 5K STARTED · wacht op serverbevestiging…');
    try {
      await onRun(activePlan);
      setMessage('RUN 5K COMPLETE · serverresultaat ontvangen en in de duel-view geladen.');
    } catch (error) {
      setMessage(`RUN 5K FAILED · ${error instanceof Error ? error.message : 'server run failed'}`);
    } finally {
      setRunningDraft(false);
    }
  };

  const clearPlan = async () => {
    if (!adminUnlocked) {
      onChange(null);
      setMessage('LOCAL PLAN CLEARED · serverplan bleef onaangeraakt omdat admin session locked is.');
      return;
    }
    if (entryA) {
      try {
        await clearPlannedSquad(entryA, activePlan.gameweek);
      } catch (error) {
        setMessage(`SERVER CLEAR FAILED · ${error instanceof Error ? error.message : 'clear failed'} · lokale plan blijft staan.`);
        return;
      }
    }
    onChange(null);
  };

  const refreshPublicPrices = async () => {
    if (!onRefreshPublicData || refreshingPrices) return;
    setRefreshingPrices(true);
    try {
      await onRefreshPublicData();
      setMessage('FPL PUBLIC DATA REFRESHED · current prices/players opnieuw geladen. Private selling prices blijven ongewijzigd en kunnen voor meerdere kandidaat-OUT spelers vooraf worden bewaard.');
    } finally {
      setRefreshingPrices(false);
    }
  };

  const swapPosition = (elementId: number, newPosition: number) => {
    const current = activePlan.picks.find((pick) => pick.element === elementId);
    const other = activePlan.picks.find((pick) => pick.position === newPosition);
    if (!current || !other || current.position === newPosition) return;
    if ((current.isCaptain || current.isViceCaptain) && newPosition > 11) {
      setMessage('C/VC PROTECT · zet eerst captain/vice op een andere starter voordat je deze speler naar de bench verplaatst.');
      return;
    }
    if ((other.isCaptain || other.isViceCaptain) && current.position > 11) {
      setMessage('C/VC PROTECT · deze swap zou captain/vice naar de bench verplaatsen. Kies eerst nieuwe C/VC.');
      return;
    }
    const picks = activePlan.picks.map((pick) => {
      if (pick.element === current.element) return { ...pick, position: newPosition, multiplier: newPosition <= 11 ? (pick.isCaptain ? 2 : 1) : 0 };
      if (pick.element === other.element) return { ...pick, position: current.position, multiplier: current.position <= 11 ? (pick.isCaptain ? 2 : 1) : 0 };
      return pick;
    });
    const starters = picks.filter((pick) => pick.position <= 11).map((pick) => elementMap.get(pick.element));
    const count = (type: number) => starters.filter((element) => element?.element_type === type).length;
    if (count(1) !== 1 || count(2) < 3 || count(2) > 5 || count(3) < 2 || count(3) > 5 || count(4) < 1 || count(4) > 3) {
      setMessage('FORMATION BLOCKED · deze swap maakt geen geldige FPL-formatie (1 GK · 3–5 DEF · 2–5 MID · 1–3 FWD).');
      return;
    }
    onChange({ ...activePlan, picks });
  };

  const setCaptain = (elementId: number, kind: 'C' | 'VC') => {
    const target = activePlan.picks.find((pick) => pick.element === elementId);
    if (!target || target.position > 11) return;
    const previousCaptain = activePlan.picks.find((pick) => pick.isCaptain) ?? null;
    const previousVice = activePlan.picks.find((pick) => pick.isViceCaptain) ?? null;
    let captainId = kind === 'C' ? elementId : previousCaptain?.element ?? null;
    let viceId = kind === 'VC' ? elementId : previousVice?.element ?? null;

    if (captainId === viceId) {
      if (kind === 'C') {
        viceId = previousCaptain?.element !== elementId && previousCaptain && previousCaptain.position <= 11 ? previousCaptain.element : null;
      } else {
        captainId = previousVice?.element !== elementId && previousVice && previousVice.position <= 11 ? previousVice.element : null;
      }
    }
    if (!captainId || captainId === viceId) captainId = activePlan.picks.find((pick) => pick.position <= 11 && pick.element !== viceId && pick.element !== elementId)?.element ?? elementId;
    if (!viceId || viceId === captainId) viceId = activePlan.picks.find((pick) => pick.position <= 11 && pick.element !== captainId && pick.element !== elementId)?.element ?? null;
    if (!viceId) return;

    const picks = activePlan.picks.map((pick) => {
      const isCaptain = pick.element === captainId;
      const isViceCaptain = pick.element === viceId;
      return { ...pick, isCaptain, isViceCaptain, multiplier: pick.position <= 11 ? (isCaptain ? 2 : 1) : 0 };
    });
    onChange({ ...activePlan, picks });
  };

  const updateSelling = (elementId: number, sellingPrice: number | null) => {
    onChange({ ...activePlan, picks: activePlan.picks.map((pick) => pick.element === elementId ? { ...pick, sellingPrice } : pick) });
  };

  const applyTransfer = () => {
    if (!outPick || !incoming) return;
    if (activePlan.cashItb === null || activePlan.cashItb === undefined) {
      setMessage('FINANCE REQUIRED · vul eerst Bram\'s echte CASH ITB in.');
      return;
    }
    if (outPick.sellingPrice === null || outPick.sellingPrice === undefined) {
      setMessage('FINANCE REQUIRED · kies in FPL Selling Price en vul alleen de echte SELL price van deze OUT-speler in.');
      return;
    }
    const sell = outPick.sellingPrice;
    const buy = priceOf(incoming) ?? 0;
    const nextCash = Math.round((activePlan.cashItb + sell - buy) * 10) / 10;
    if (nextCash < -0.001) {
      setMessage(`ILLEGAL · budget tekort £${Math.abs(nextCash).toFixed(1)}m.`);
      return;
    }
    const clubCount = activePlan.picks.filter((pick) => pick.element !== outPick.element && elementMap.get(pick.element)?.team === incoming.team).length;
    if (clubCount >= 3) {
      setMessage('ILLEGAL · maximaal 3 spelers van dezelfde club.');
      return;
    }
    const picks = activePlan.picks.map((pick) => pick.element === outPick.element ? {
      ...pick,
      element: incoming.id,
      purchasePrice: buy,
      sellingPrice: buy,
    } : pick);
    const transfersMade = (activePlan.transfersMade ?? 0) + 1;
    const transfer = transferState(activePlan, undefined, transfersMade);
    onChange({
      ...activePlan,
      ...transfer,
      cashItb: Math.max(0, nextCash),
      routeLabel: `${outElement?.web_name ?? outElement?.second_name ?? 'OUT'}→${incoming.web_name ?? incoming.second_name}`,
      picks,
    });
    setOutId('');
    setInId('');
    setMessage(`Route toegepast · IN prijs automatisch FPL ${fmtPrice(buy)} · FT left ${transfer.ftCount} · hit -${transfer.hitCost} · cash ${fmtPrice(Math.max(0, nextCash))}. Klik RUN 5K wanneer je klaar bent met editen.`);
  };

  return (
    <section className="panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="eyebrow">DECISION LAB · PLANNED GW{activePlan.gameweek}</div>
          <h2 className="mt-1 text-lg font-black text-white">{activePlan.routeLabel || 'PLANNED ROUTE'}</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="captain-summary captain">C · {captainName}</span>
            <span className="captain-summary vice">VC · {viceName}</span>
            <span className={draftChanged ? 'draft-status changed' : 'draft-status synced'}>{draftChanged ? 'DRAFT CHANGED · RUN 5K' : 'SIM = CURRENT DRAFT'}</span>
            <span className="draft-status public-price">FPL PRICES · AUTO 5m</span>
          </div>
          <p className="mt-2 text-[10px] text-slate-600">Edits blijven lokaal en veroorzaken geen nieuwe 5K/netwerk-run. Alleen RUN 5K of SAVE + RUN activeert de nieuwe draft.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary" onClick={() => void savePlan(true)} disabled={saving || !adminUnlocked}>{saving ? 'SAVING…' : adminUnlocked ? 'SAVE + RUN 5K' : 'UNLOCK TO RUN'}</button>
          <button type="button" className="btn-secondary" onClick={() => void runDraft()} disabled={!adminUnlocked || runningDraft}>{runningDraft ? 'RUNNING 5K…' : 'RUN DRAFT 5K'}</button>
          <button type="button" className="btn-secondary" onClick={() => void savePlan(false)} disabled={saving || !adminUnlocked}>SAVE ONLY</button>
          <button type="button" className="btn-secondary" onClick={() => void refreshPublicPrices()} disabled={!onRefreshPublicData || refreshingPrices}>{refreshingPrices ? 'REFRESHING…' : 'REFRESH FPL'}</button>
          <button type="button" className="btn-secondary" onClick={initialize}>RESET TO PUBLIC</button>
          <button type="button" className="btn-secondary" onClick={() => void clearPlan()}>CLEAR</button>
        </div>
      </div>

      {adminControls}

      <div className="mt-4 grid gap-2 md:grid-cols-6">
        <label><span className="input-label">ROUTE</span><input className="input" value={activePlan.routeLabel ?? ''} onChange={(event: ChangeEvent<HTMLInputElement>) => patchPlan({ routeLabel: event.target.value })} /></label>
        <label><span className="input-label">START FT · CONFIRM</span><input className="input" type="number" min={0} max={5} value={activePlan.initialFtCount ?? ((activePlan.ftCount ?? 1) + (activePlan.transfersMade ?? 0))} onChange={(event: ChangeEvent<HTMLInputElement>) => setStartingFt(Number(event.target.value))} /></label>
        <label><span className="input-label">FT LEFT</span><input className="input" readOnly value={activePlan.ftCount ?? 0} /></label>
        <label><span className="input-label">HIT</span><input className="input" readOnly value={`-${activePlan.hitCost ?? 0}`} /></label>
        <label><span className="input-label">CASH ITB (£m)</span><input className="input" type="number" min={0} step="0.1" value={activePlan.cashItb ?? ''} onChange={(event: ChangeEvent<HTMLInputElement>) => patchPlan({ cashItb: event.target.value === '' ? null : Number(event.target.value) })} /></label>
        <label><span className="input-label">CHIP</span><select className="input" value={activePlan.activeChip ?? ''} onChange={(event: ChangeEvent<HTMLSelectElement>) => setChip(event.target.value || null)}><option value="">NONE</option><option value="wildcard">WILDCARD</option><option value="freehit">FREE HIT</option><option value="bboost">BENCH BOOST</option><option value="3xc">TRIPLE CAPTAIN</option></select></label>
      </div>

      {!outId && (
        <div className="mt-3 rounded-xl border border-emerald-800/40 bg-emerald-950/10 p-3 text-[10px] leading-5 text-emerald-300">
          ROLL / LINEUP READY · publieke CURRENT prices staan automatisch in de tabel. Vul alleen private SELL prices in voor spelers die je als mogelijke OUT wilt laten onderzoeken; meerdere kandidaat-OUT prices blijven in de Planned Squad bewaard.
        </div>
      )}

      <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2"><div className="section-title">SMART TRANSFER ROUTE</div><div className="text-[9px] text-slate-600">IN/current price = FPL Public JSON · SELL = jouw private FPL selling price</div></div>
        <div className="mt-2 grid gap-2 xl:grid-cols-[1.15fr_1.15fr_.7fr_.7fr_.7fr_auto]">
          <select className="input" value={outId} onChange={(event: ChangeEvent<HTMLSelectElement>) => { setOutId(event.target.value); setInId(''); }}><option value="">OUT…</option>{ordered.map((pick) => { const e = elementMap.get(pick.element); return <option key={pick.element} value={pick.element}>{e?.web_name ?? e?.second_name ?? pick.element} · current {fmtPrice(priceOf(e))}</option>; })}</select>
          <select className="input" value={inId} onChange={(event: ChangeEvent<HTMLSelectElement>) => setInId(event.target.value)} disabled={!outId}><option value="">IN · same position…</option>{candidates.map((element) => <option key={element.id} value={element.id}>{element.web_name || element.second_name} · {fmtPrice(priceOf(element))}</option>)}</select>
          <div className="smart-finance-box"><span>OUT CURRENT</span><strong>{fmtPrice(outCurrent)}</strong></div>
          <label><span className="input-label">OFFICIAL SELL</span><input className="input" type="number" min={0} step="0.1" disabled={!outPick} value={outPick?.sellingPrice ?? ''} onChange={(event: ChangeEvent<HTMLInputElement>) => { if (outPick) updateSelling(outPick.element, event.target.value === '' ? null : Number(event.target.value)); }} placeholder="bv 5.5" /></label>
          <div className="smart-finance-box"><span>IN / CASH AFTER</span><strong>{fmtPrice(inCurrent)} · {fmtPrice(cashAfter)}</strong></div>
          <button type="button" className="btn-primary" onClick={applyTransfer} disabled={!outPick || !incoming}>APPLY</button>
        </div>
        {outPick && outPick.sellingPrice == null && <div className="mt-2 text-[10px] text-amber-300">Alleen nog SELL invullen voor {outElement?.web_name ?? outElement?.second_name}. Geen buy/sell-invoer voor de andere 14 spelers nodig.</div>}
        {message && <p className="mt-2 text-[10px] text-amber-300">{message}</p>}
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-[11px]">
          <thead className="text-[9px] uppercase tracking-[0.12em] text-slate-600"><tr><th className="pb-2">Pos</th><th>Player</th><th>XI/Bench</th><th>C / VC</th><th>Current £</th><th>Private sell £</th><th>xMIN / Start</th><th>Model</th></tr></thead>
          <tbody>{ordered.map((pick) => {
            const element = elementMap.get(pick.element);
            const player = duel.players.find((row) => row.elementId === pick.element);
            return <tr key={pick.element} className={`border-t border-slate-800/70 ${pick.isCaptain ? 'captain-row' : pick.isViceCaptain ? 'vice-row' : ''}`}>
              <td className="py-2"><select className="input !h-8 !py-0" value={pick.position} onChange={(event: ChangeEvent<HTMLSelectElement>) => swapPosition(pick.element, Number(event.target.value))}>{(element?.element_type === 1 ? [1, 12] : [2,3,4,5,6,7,8,9,10,11,13,14,15]).map((position) => <option key={position} value={position}>{position}</option>)}</select></td>
              <td className="font-bold text-slate-300">{element?.web_name ?? element?.second_name ?? pick.element}</td>
              <td><span className={pick.position <= 11 ? 'badge-sim' : 'mode-chip'}>{pick.position <= 11 ? 'XI' : element?.element_type === 1 ? 'BENCH GK' : `BENCH ${Math.max(1, pick.position - 12)}`}</span></td>
              <td><div className="flex gap-1"><button type="button" className={pick.isCaptain ? 'btn-micro captain-active' : 'btn-micro'} onClick={() => setCaptain(pick.element, 'C')} disabled={pick.position > 11} title={pick.position > 11 ? 'Captain must be in Starting XI' : 'Set captain'}>{pick.isCaptain ? 'C ✓' : 'C'}</button><button type="button" className={pick.isViceCaptain ? 'btn-micro vice-active' : 'btn-micro'} onClick={() => setCaptain(pick.element, 'VC')} disabled={pick.position > 11} title={pick.position > 11 ? 'Vice-captain must be in Starting XI' : 'Set vice-captain'}>{pick.isViceCaptain ? 'VC ✓' : 'VC'}</button></div></td>
              <td className="font-black tabular-nums text-slate-300">{fmtPrice(priceOf(element))}</td>
              <td><input className="input !h-8 !w-24 !py-0" type="number" min={0} step="0.1" value={pick.sellingPrice ?? ''} onChange={(event: ChangeEvent<HTMLInputElement>) => updateSelling(pick.element, event.target.value === '' ? null : Number(event.target.value))} placeholder="sell" aria-label={`${element?.web_name ?? element?.second_name ?? pick.element} selling price`} /></td>
              <td className="text-[10px] tabular-nums text-slate-500">{player ? `${player.minuteDistribution.expectedMinutes.toFixed(0)}m · ${(player.minuteDistribution.pStart * 100).toFixed(0)}%` : '—'}</td>
              <td className="text-[9px] text-slate-600">{player ? `MODEL C${player.confidence.modelConfidence} · DATA ${player.confidence.dataCompleteness}` : 'projection next run'}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>
    </section>
  );
}
